// B122 Phase 2 — pure extraction of TaxPage:1642 landTaxGroups inline.
// Behavior-equivalent: same input/output as today; same per-state/per-entity
// grouping, same surcharge rules (VIC COVID-19 levy, SA discretionary trust
// surcharge, ACT per-parcel + fixed charges, NSW entry charge), same return
// shape (LandTaxGroupRow[] sorted by estimatedTax desc).
//
// Phase 2 of B122 needed the aggregation accessible from TrustLandTaxPanel
// (via entityTaxData). Extracted to a shared helper as part of the SSOT
// mandate. TaxPage Personal-branch Land Tax tab continues to consume this
// helper at the same call site, with no shape change.
//
// Note on signature: the audit doc R4.1 sketched a simplified
// Map<string, { byState; total }> shape, but the TaxPage consumer needs
// the rich per-row state-specific surcharge fields. Audit's R4.6 binding
// constraint ("Same input/output as today. Behavior-equivalence verified
// by snapshot test") rules — keep the rich shape.

import type { PropertyRecord } from "../types/portfolio";
import { LAND_TAX_RULES, estimateACTLandTax } from "./state-tax-rules-2025-26";

/**
 * Minimal shape required from each propertyTaxData row. Accepts the full
 * TaxPage propertyTaxData[] entries via structural typing.
 */
export interface LandTaxInputRow {
  p: PropertyRecord;
  entityType: string;
  entityName: string;
}

export interface LandTaxGroupRow {
  state: string;
  entityName: string;
  entityType: string;
  /** structureId of the entity, or null for personal-name (no structure). */
  structureId: string | null;
  propertyCount: number;
  totalLandValue: number;       // ownership-adjusted sum
  threshold: number;
  baseTax: number;              // base bracket calculation (pre-surcharges)
  vicCovidLevy?: number;        // VIC only
  saTrustSurcharge?: number;    // SA discretionary trusts only
  actFixedCharges?: number;     // ACT only — per-property fixed charges
  estimatedTax: number;         // total including all surcharges
  landValues: number[];         // ownership-adjusted per-property land values (used by ACT per-parcel calc)
  allHaveULV: boolean;          // true when every property in the group has an unimprovedLandValue set
}

/**
 * Per-state base-bracket land tax (NSW $100 entry charge applied when
 * positive). Mirrors the legacy estimateLandTax inline in TaxPage:448
 * exactly (extracted as part of B122 Phase 2 SSOT mandate).
 */
function estimateLandTax(
  landValue: number,
  entityType: string,
  state: string,
): number {
  const rule = LAND_TAX_RULES[state];
  if (!rule || rule.brackets.length === 0) return 0;

  const isTrust = entityType.toLowerCase().includes("trust") || entityType === "Company / Trust";
  const isCompany = entityType === "Company";
  const threshold = isTrust ? rule.trustThreshold : isCompany ? rule.companyThreshold : rule.individualThreshold;

  if (landValue <= threshold) return 0;

  let tax = 0;
  for (const bracket of rule.brackets) {
    if (bracket.to === Infinity) {
      if (landValue > bracket.from) {
        tax += Math.max(0, landValue - Math.max(threshold, bracket.from)) * bracket.rate;
      }
      break;
    }
    if (landValue > bracket.from) {
      const upper = Math.min(bracket.to, landValue) - Math.max(threshold, bracket.from);
      if (upper > 0) tax += upper * bracket.rate;
    }
  }
  // NSW $100 entry charge once over threshold
  if (rule.entryCharge && tax > 0) {
    tax += rule.entryCharge;
  }
  return Math.round(tax);
}

/**
 * Aggregate land tax across the supplied propertyTaxData rows, grouping by
 * (state × entity). Pure. No DOM. No fetch. Same output as the legacy
 * TaxPage:1642 inline useMemo.
 *
 * Per-trust filtering is done downstream (entityTaxData filters by structureId).
 *
 * Sort order: estimatedTax descending (preserved from legacy).
 */
export function landTaxAggregation(
  propertyTaxData: LandTaxInputRow[],
): LandTaxGroupRow[] {
  const groups: Record<string, LandTaxGroupRow> = {};
  for (const d of propertyTaxData) {
    const state = d.p.state ?? "Unknown";
    const structureId = d.p.structureId ?? null;
    const key = `${state}__${structureId ?? "personal"}`;
    if (!groups[key]) {
      const rule = LAND_TAX_RULES[state];
      const isTrust = d.entityType.toLowerCase().includes("trust") || d.entityType === "Company / Trust";
      const isCompany = d.entityType === "Company";
      const threshold = rule
        ? (isTrust ? rule.trustThreshold : isCompany ? rule.companyThreshold : rule.individualThreshold)
        : Infinity;
      groups[key] = {
        state,
        entityName: d.entityName,
        entityType: d.entityType,
        structureId,
        propertyCount: 0,
        totalLandValue: 0,
        threshold,
        baseTax: 0,
        estimatedTax: 0,
        landValues: [],
        allHaveULV: true,
      };
    }
    const hasULV = typeof d.p.unimprovedLandValue === "number" && d.p.unimprovedLandValue > 0;
    const rawLandValue = hasULV ? (d.p.unimprovedLandValue as number) : (d.p.currentValue ?? 0);
    const ownedValue = rawLandValue * ((d.p.ownershipPercent ?? 100) / 100);
    groups[key].propertyCount++;
    groups[key].totalLandValue += ownedValue;
    groups[key].landValues.push(ownedValue);
    if (!hasULV) groups[key].allHaveULV = false;
  }

  for (const group of Object.values(groups)) {
    const rule = LAND_TAX_RULES[group.state];
    if (!rule) continue;

    if (group.state === "ACT") {
      const act = estimateACTLandTax(group.landValues, rule);
      group.baseTax = act.bracketTotal;
      group.actFixedCharges = act.fixedChargesTotal;
      group.estimatedTax = act.bracketTotal + act.fixedChargesTotal;
      continue;
    }

    group.baseTax = estimateLandTax(group.totalLandValue, group.entityType, group.state);
    let total = group.baseTax;

    if (group.state === "VIC") {
      const taxable = group.totalLandValue - group.threshold;
      if (taxable > 0) {
        const rate = taxable > 100_000 ? 0.0025 : 0.001;
        const levy = Math.round(975 + taxable * rate);
        group.vicCovidLevy = levy;
        total += levy;
      }
    }

    const isTrust =
      group.entityType.toLowerCase().includes("trust") || group.entityType === "Company / Trust";
    if (group.state === "SA" && isTrust && group.totalLandValue > 0) {
      const surcharge = Math.round(group.totalLandValue * 0.005);
      group.saTrustSurcharge = surcharge;
      total += surcharge;
    }

    group.estimatedTax = total;
  }

  return Object.values(groups).sort((a, b) => b.estimatedTax - a.estimatedTax);
}
